import { Accordion } from 'common/accordion/accordion.js';
import { Button } from 'common/button.js';
import { IconV1 } from 'common/icon/iconV1.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { Tabs } from 'common/tabs/tab.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { Row, SmallTable } from 'components/tables/accordion-table.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { ReactNode, useContext } from 'react';
import { IModule } from 'redux-state/layout/types.js';
import { LinkTarget } from 'types/navigation.js';
import { DeepReadonly } from 'utilities/data-structures/deepReadonly.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { predicateString } from 'utilities/isType.js';
import { setDefaultTitle } from 'utilities/text/titleDefault.js';

enum RADIO_TYPES {
  ZKLH = 'ZKLH',
  HF = 'HF',
  INMARSAT = 'INMARSAT',
}

type IRadioTab = DeepReadonly<{
  name: string;
  date: string;
  headers: string[];
  values: Array<string[] | string[][]>;
  type: RADIO_TYPES;

  disclaimer?: string;
  pdf?: string;
  link?: {
    url: string;
    label: string;
  };
}>;

interface ITrafficCameraConfig {
  tabs: IRadioTab[];
}

const RadioScheduleInner = ({
  tabs,
  activeTabIndex,
}: {
  activeTabIndex: number;
  tabs: ReadonlyArray<Readonly<IRadioTab>>;
}) => {
  const isLarge = useContext(ColumnLayoutContext) !== '1-column';
  const activeTab = tabs?.[activeTabIndex] as IRadioTab | undefined;
  if (activeTab) {
    const { headers, values, type } = activeTab;

    if (isLarge) {
      return (
        <table key={activeTabIndex} className="AccordionTable">
          {/* ZKLH Has this main header, along with a secondary header below which are just bold tds */}
          {type === RADIO_TYPES.ZKLH && (
            <thead className="AccordionTable-head">
              <Row>
                <th className="h4 u-sm-hidden">PRODUCT</th>
                {/* This th needs to cover the rest of the columns so it's the headers length - 1 (the first column is empty for product) */}
                <th className="h4 u-sm-hidden" colSpan={headers.length - 1}>
                  ZKLF TRANSMISSION TIMES (UTC) & FREQUENCY
                </th>
              </Row>
            </thead>
          )}

          {/* If type is not ZKLH then it's just a normal header so loop through them and create a th for each */}
          {type !== RADIO_TYPES.ZKLH && (
            <thead className="AccordionTable-head">
              <Row>
                {headers.map((val: string, idx: number) => (
                  <th key={idx} className="h4 u-sm-hidden">
                    {val}
                  </th>
                ))}
              </Row>
            </thead>
          )}

          <tbody className="AccordionTable-body">
            {/* ZKLH has a secondary header but it's not actually a th, it's just a td with bold text */}
            <Row>
              {headers.map((val: string, idx: number) => {
                if (type === RADIO_TYPES.ZKLH) {
                  return (
                    <td key={idx} className="h4">
                      {val}
                    </td>
                  );
                }
              })}
            </Row>
            {values.map((row, rowIdx: number) => (
              <Row key={rowIdx}>
                {row.map((data, idx: number) => {
                  let toRender: ReactNode = data;
                  // This nested array should use mobile doc but I'm unsure of what the api can achieve
                  if (!predicateString(data)) {
                    toRender = data.map((d: string, dIdx) => {
                      if (dIdx === 0) {
                        return (
                          <b className="small u-block" key={dIdx}>
                            {d}
                          </b>
                        );
                      }
                      return (
                        <span className="small u-inlineBlock" key={dIdx}>
                          {d}
                        </span>
                      );
                    });
                  }

                  if (idx === 0) {
                    return (
                      <th key={idx} className="h4">
                        {toRender}
                      </th>
                    );
                  }
                  return (
                    <td key={idx} className="small">
                      {toRender}
                    </td>
                  );
                })}
              </Row>
            ))}
          </tbody>
        </table>
      );
    }

    const [firstHeader, ...remainingHeaders] = headers;
    // Grab the remaining headers minus the first column which will now be used as content

    // Headers are the first column of the values in small mode
    // const firstHeader = getFirstOfArr(headers);
    const smallHeaders = values.map((row, rowIdx: number) => {
      const firstHeaderIsEmpty = firstHeader === '';
      return (
        <span className="small" key={rowIdx}>
          {!firstHeaderIsEmpty && <strong>{firstHeader} </strong>}
          {firstHeaderIsEmpty ? <strong>{row?.[0]}</strong> : <>{row?.[0]}</>}
        </span>
      );
    });

    const smallBodies = values.map((row, rowIdx: number) => {
      const content = remainingHeaders.map((head: string, headIdx: number) => {
        const rowData = row[headIdx + 1];
        let toRender: ReactNode = null;
        if (!predicateString(rowData)) {
          toRender = rowData.map((d: string, dIdx) => {
            if (dIdx === 0) {
              return (
                <b className="small u-inlineBlock" key={dIdx}>
                  {d}
                </b>
              );
            }
            return (
              <span className="small u-inlineBlock" key={dIdx}>
                {d}
              </span>
            );
          });
        }

        if (!toRender) {
          toRender = rowData as string;
        }

        return (
          <Row key={headIdx}>
            <th className="h4">{head}</th>
            {/* The first value in row is the rowName, depending on the api this will probably need to change */}
            <td className="small">{toRender}</td>
          </Row>
        );
      });

      return (
        <SmallTable key={rowIdx}>
          {type === RADIO_TYPES.ZKLH && (
            <Row>
              <th className="h4">FREQUENCY (KHZ)</th>
              <th className="h4">ZKLF-TRANSMISSION-TIMES (UTC)</th>
            </Row>
          )}
          {content}
        </SmallTable>
      );
    });

    return (
      <Accordion
        key={activeTabIndex}
        dataComponent="radio-schedule"
        restrictToOnlyOneChildOpen={true}
        className="Accordion--blue"
        headers={smallHeaders}
        bodies={smallBodies}
      />
    );
  }
  messageLog.warning('RadioScheduleInner: No activeTab found');
  return null;
};

export const RadioSchedule = ({ config, isDrawer, modulePath }: IModule<ITrafficCameraConfig>) => {
  const { tabs } = config;

  return (
    <Tile dataModule="radio-schedule" config={config}>
      <div className="Tile-body Tile-body--noPadding">
        <TileBodyHeaderV2
          isDrawer={isDrawer}
          modulePath={modulePath}
          config={setDefaultTitle(config, 'Radio Schedule')}
        />
        <Tabs
          headers={tabs.map((tab: IRadioTab) => tab.name)}
          renderActiveTab={(activeTabIndex: number) => {
            const activeTab = tabs[activeTabIndex];
            const { date, disclaimer, link, pdf } = activeTab;
            return (
              <>
                <RadioScheduleInner activeTabIndex={activeTabIndex} tabs={tabs} />
                <div className="u-p-responsive u-md-flex u-md-flexJustifyBetween">
                  <div className="u-sm-textCenter u-sm-mB-xs">
                    <p className="fineprint u-textGrey u-mT-0 u-mB-0">{date}</p>
                    {disclaimer && <p className="fineprint u-textGrey u-mT-0 u-mB-0">{disclaimer}</p>}
                    {link && (
                      <LinkFactoryReactRouter className="fineprint u-linkClean" href={link.url}>
                        {link.label}
                      </LinkFactoryReactRouter>
                    )}
                  </div>
                  {pdf && (
                    <Button
                      classes="Button--secondary u-sm-block"
                      ariaLabel="download"
                      action={pdf}
                      target={LinkTarget.BLANK}
                    >
                      <div>
                        {'Download PDF'}
                        <div className="u-pL-xs u-inlineBlock svg--font-inline">
                          <IconV1 icon={'download'} />
                        </div>
                      </div>
                    </Button>
                  )}
                </div>
              </>
            );
          }}
        />
      </div>
    </Tile>
  );
};
